import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getJobs } from '@/lib/api/jobs';
import type { Job } from '@/lib/api/jobs';
import { jobPath } from '@/lib/utils/navPaths';

interface JobsListPanelProps {
  onNewJob?: () => void;
}

export const JobsListPanel = ({ onNewJob }: JobsListPanelProps) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [jobs, setJobs] = useState<Job[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadJobs();
  }, []);

  const loadJobs = async () => {
    try {
      setLoading(true);
      const response = await getJobs({});
      setJobs(response.jobs);
    } catch (error) {
      console.error('Failed to load jobs:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredJobs = jobs.filter(job => {
    const matchesSearch = !searchTerm || 
      job.jobNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      job.client?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'All' || job.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="flex flex-col h-full bg-white">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Jobs</h2>
        {onNewJob && (
          <button
            onClick={onNewJob}
            className="flex w-full min-w-[84px] max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-lg h-10 px-4 bg-primary text-white gap-2 text-sm font-bold leading-normal tracking-[0.015em] mb-4 hover:bg-primary/90 transition-colors"
          >
            <span className="material-symbols-outlined text-base">add</span>
            <span className="truncate">New Job</span>
          </button>
        )}
        <label className="flex flex-col w-full mb-3">
          <div className="flex w-full flex-1 items-stretch rounded-lg h-11">
            <div className="text-gray-500 flex border border-r-0 border-gray-300 bg-gray-50 items-center justify-center pl-4 rounded-l-lg">
              <span className="material-symbols-outlined">search</span>
            </div>
            <input
              className="form-input flex w-full min-w-0 flex-1 resize-none overflow-hidden rounded-r-lg text-gray-900 focus:outline-0 focus:ring-1 focus:ring-primary border-gray-300 border bg-white h-full placeholder:text-gray-500 px-4 pl-2 text-sm font-normal leading-normal focus:border-primary"
              placeholder="Search jobs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </label>
        <select
          className="w-full form-select rounded-lg border-gray-300 bg-gray-50 text-gray-900 h-10 px-4 text-sm font-medium focus:outline-0 focus:ring-2 focus:ring-primary"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option>All</option>
          <option>In Progress</option>
          <option>Scheduled</option>
          <option>Completed</option>
          <option>On Hold</option>
        </select>
      </div>
      <div className="flex-1 overflow-y-auto">
        {loading ? (
          <div className="p-4 text-center text-gray-600">Loading jobs...</div>
        ) : filteredJobs.length === 0 ? (
          <div className="p-4 text-center text-gray-600">No jobs found</div>
        ) : (
          filteredJobs.map((job) => (
            <div
              key={job.id}
              onClick={() => navigate(jobPath(job.clientId, job.id))}
              className="flex items-center gap-4 px-4 min-h-[72px] py-2 justify-between cursor-pointer border-l-4 border-transparent hover:bg-gray-100"
            >
              <div className="flex items-center gap-4">
                <div className="bg-primary/10 text-primary rounded-full h-12 w-12 flex items-center justify-center flex-shrink-0">
                  <span className="material-symbols-outlined">construction</span>
                </div>
                <div className="flex flex-col justify-center min-w-0">
                  <p className="text-gray-900 text-base font-semibold leading-normal line-clamp-1">
                    {job.jobNumber}
                  </p>
                  <p className="text-gray-600 text-sm font-normal leading-normal line-clamp-2">
                    {job.title}
                  </p>
                  <p className="text-gray-500 text-xs font-normal leading-normal line-clamp-1">
                    {job.client}
                  </p>
                </div>
              </div>
              <div className={`shrink-0 px-2 py-1 rounded text-xs font-medium ${
                job.status === 'Completed' ? 'bg-green-100 text-green-700' :
                job.status === 'In Progress' ? 'bg-blue-100 text-blue-700' :
                job.status === 'Scheduled' ? 'bg-purple-100 text-purple-700' :
                'bg-gray-100 text-gray-700'
              }`}>
                {job.status}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};
